/**
 * The Brand DNA during the onboarding: when step 1 reads the brand's website,
 * and what the side panel says about it meanwhile. Pure, so the two decisions
 * are tested without a network.
 */

/** The last reading of a website started from the onboarding, as the mutation cache holds it. */
export type DnaGeneration = {
  status: 'idle' | 'pending' | 'success' | 'error';
  websiteUrl: string | null;
};

export type SavedBrandDna = {
  brandDna: string | null;
  brandDnaSourceUrl: string | null;
};

export type DnaPanelStatus = 'empty' | 'loading' | 'ready' | 'retry';

/**
 * Read a website once: when the brand has no Brand DNA from it yet and no
 * reading of it is running or done. A site that could not be read is not read
 * again here; the companion's Brand DNA gate takes it up later.
 */
export function shouldGenerateDna(
  websiteUrl: string | null,
  saved: SavedBrandDna | undefined,
  last: DnaGeneration | undefined
): boolean {
  if (!websiteUrl) return false;
  if (saved?.brandDna && saved.brandDnaSourceUrl === websiteUrl) return false;
  if (last && last.websiteUrl === websiteUrl && last.status !== 'idle') return false;
  return true;
}

/**
 * What the brand card says about the Brand DNA.
 *
 * `analysisExpected` is true on step 1 only, where a website being typed is
 * about to be read. Once the brand left that step, a website with no Brand DNA
 * and no reading under way is one that will be read later, never one being
 * analysed now.
 */
export function dnaPanelStatus({
  websiteUrl,
  savedDna,
  generation,
  analysisExpected,
}: {
  websiteUrl: string | null;
  savedDna: string | null;
  generation?: DnaGeneration;
  analysisExpected: boolean;
}): DnaPanelStatus {
  if (generation?.status === 'pending') return 'loading';
  if (savedDna) return 'ready';
  if (!websiteUrl) return 'empty';
  if (generation?.status === 'error' && generation.websiteUrl === websiteUrl) return 'retry';
  if (generation?.status === 'success') return 'loading';
  return analysisExpected ? 'loading' : 'retry';
}
